import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { of } from "rxjs";
import { catchError, first } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { EntityModel } from "../models/entity.model";
import { ShowcaseLayoutModel } from "src/app/shared/models/showcase-layout.model";

export type EntityBackupExportType =
  | "properties"
  | "stores"
  | "users"
  | "leads"
  | "deals"
  | "sales"
  | "contacts";

export interface GeocodeResult {
  lat: string;
  lon: string;
  display_name?: string;
  boundingbox?: string[];
}

/** Pesquisa Nominatim: livre (`q`) ou estruturada (os restantes campos). */
export interface GeocodeQuery {
  q?: string;
  street?: string;
  city?: string;
  county?: string;
  state?: string;
  postalcode?: string;
  country?: string;
}

/** Resposta da geoapi.pt para um código postal português (CP7). */
export interface PortugalPostalCodeResult {
  CP?: string;
  Distrito?: string;
  Concelho?: string;
  Localidade?: string;
  /** [lat, lng] do troço */
  centro?: number[];
  /** [lat, lng] do troço */
  centroide?: number[];
  partes?: Record<string, string>[];
  /** moradas individuais com número de porta e [lat, lng] */
  pontos?: { rua?: string; casa?: string; coordenadas?: number[] }[];
}

@Injectable({
  providedIn: "root",
})
export class EntitiesService {
  constructor(private http: HttpClient) {}

  /**
   * Pesquisa no Nominatim (OpenStreetMap), livre ou estruturada. Só envia os
   * parâmetros preenchidos. Qualquer erro de rede/429 devolve [] para o
   * chamador tratar como "sem resultado".
   */
  geocode(query: GeocodeQuery) {
    let params = new HttpParams().set("format", "json").set("limit", "1");

    Object.entries(query).forEach(([key, value]) => {
      const text = (value ?? "").toString().trim();
      if (text) params = params.set(key, text);
    });

    return this.http
      .get<GeocodeResult[]>("https://nominatim.openstreetmap.org/search", {
        params,
      })
      .pipe(
        first(),
        catchError(() => of([] as GeocodeResult[])),
      );
  }

  /**
   * Código postal português (CP7) via geoapi.pt (dados CTT/INE): devolve o
   * centro do troço de rua e os pontos com número de porta. null se o código
   * não existir ou a chamada falhar.
   */
  geocodePortugalPostalCode(postalCode: string) {
    return this.http
      .get<PortugalPostalCodeResult>(
        `https://json.geoapi.pt/cp/${encodeURIComponent(postalCode)}`,
      )
      .pipe(
        first(),
        catchError(() => of(null as PortugalPostalCodeResult | null)),
      );
  }

  /***
   * Get All entities
   */
  getAll(page: number, search = "") {
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/entities`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of({ imoModules: [], total: 0 });
        }),
      );
  }

  getCountries() {
    return this.http
      .get<[]>(`${environment.apiUrl}/countries/include-districts`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return [];
        }),
      );
  }

  getRoleMaster() {
    return this.http
      .get<any>(`${environment.apiUrl}/roles/master`, { withCredentials: true })
      .pipe(first());
  }

  deleteByIds(ids: any) {
    return this.http
      .delete<any>(`${environment.apiUrl}/entities`, {
        withCredentials: true,
        body: { ids },
      })
      .pipe(first());
  }

  getSubscritpionsPlans(contryId: number) {
    return this.http
      .get<
        []
      >(`${environment.apiUrl}/subscriptions-plans/country/${contryId}`, { withCredentials: true })
      .pipe(
        first(),
        catchError((error) => {
          return [];
        }),
      );
  }

  getLanguages() {
    return this.http
      .get<[]>(`${environment.apiUrl}/languages`, { withCredentials: true })
      .pipe(
        first(),
        catchError((error) => {
          return [];
        }),
      );
  }

  getAllPortals() {
    return this.http
      .get<[]>(`${environment.apiUrl}/portals`, { withCredentials: true })
      .pipe(
        first(),
        catchError((error) => {
          return [];
        }),
      );
  }

  // entity
  getEntity(id: number) {
    return this.http
      .get<EntityModel>(`${environment.apiUrl}/entities/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  createEntity(data: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/entities`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateEntity(id: number, data: any) {
    return this.http.patch<any>(`${environment.apiUrl}/entities/${id}`, data, {
      withCredentials: true,
    });
  }

  getShowcaseLayouts(entityId: number) {
    return this.http
      .get<ShowcaseLayoutModel[]>(
        `${environment.apiUrl}/showcase-sheet/entities/${entityId}/layouts`,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  createShowcaseLayout(entityId: number, data: FormData) {
    return this.http
      .post<ShowcaseLayoutModel>(
        `${environment.apiUrl}/showcase-sheet/entities/${entityId}/layouts`,
        data,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  updateShowcaseLayout(entityId: number, layoutId: number, data: FormData) {
    return this.http
      .patch<ShowcaseLayoutModel>(
        `${environment.apiUrl}/showcase-sheet/entities/${entityId}/layouts/${layoutId}`,
        data,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  deleteShowcaseLayout(entityId: number, layoutId: number) {
    return this.http
      .delete<any>(
        `${environment.apiUrl}/showcase-sheet/entities/${entityId}/layouts/${layoutId}`,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  deleteEntity(id: number) {
    return this.http
      .delete<any>(`${environment.apiUrl}/entities/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  // stores
  getStoresByEntity(entityId: number, page: number, search: string) {
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/entities/${entityId}/stores`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of({ stores: [], total: 0 });
        }),
      );
  }

  createStore(data: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/entities/store`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateStore(storeId: any, data: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}/entities/${storeId}/store`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteStores(storeId: any, storeIds: any[]) {
    return this.http
      .delete<any>(`${environment.apiUrl}/entities/${storeId}/stores`, {
        withCredentials: true,
        body: { storeIds },
      })
      .pipe(first());
  }

  // users
  getUsersByEntity(
    entityId: number,
    page: number,
    search: string,
    filters: any = {},
  ) {
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    if (filters.status) params = params.set("status", filters.status);
    if (filters.stores) params = params.set("stores", filters.stores);
    if (filters.groups) params = params.set("groups", filters.groups);

    return this.http
      .get<any>(`${environment.apiUrl}/entities/${entityId}/users`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of({ stores: [], total: 0 });
        }),
      );
  }

  createUser(data: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/entities/user`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateUser(userId: any, data: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}/entities/${userId}/user`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteUsers(entityId: any, userIds: any[]) {
    return this.http
      .delete<any>(`${environment.apiUrl}/entities/${entityId}/users`, {
        withCredentials: true,
        body: { userIds },
      })
      .pipe(first());
  }

  /***
   * Get All Roles
   */
  getAllRoles() {
    return this.http
      .get<any>(`${environment.apiUrl}/roles`, { withCredentials: true })
      .pipe(first());
  }

  updateSubscriptionStatus(entityId: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/entities/${entityId}/subscription-status`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  changeSubscriptionPlan(entityId: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/entities/${entityId}/change-subscription`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  updateSubscription(entityId: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/entities/${entityId}/update-subscription`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  updateSubscriptionRenew(entityId: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/entities/${entityId}/subscription-renew`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /***
   * Get data to create form user
   */
  getDataToCreateFormUser() {
    return this.http
      .get<any>(`${environment.apiUrl}/entities/get-data-to-create-user-form`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  supportAs(data: any) {
    return this.http
      .post<any>(`${environment.apiUrl}/login-as`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Upload entity contract
   * @param entityId
   * @param formData
   * @returns
   */
  uploadEntityContract(entityId: number, formData: FormData) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/entities/${entityId}/contract`,
        formData,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  /**
   * Delete entity contract
   * @param entityId
   * @returns
   */
  deleteEntityContract(entityId: number) {
    return this.http
      .delete<any>(`${environment.apiUrl}/entities/${entityId}/contract`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Get entity contract
   * @param entityId
   * @returns
   */
  getEntityContract(entityId: number) {
    return this.http
      .get<any>(`${environment.apiUrl}/entities/${entityId}/contract`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of(null);
        }),
      );
  }

  exportEntityBackup(
    entityId: number,
    type: EntityBackupExportType,
  ) {
    return this.http
      .get(`${environment.apiUrl}/entities/${entityId}/backup/export/${type}`, {
        withCredentials: true,
        observe: "response",
        responseType: "blob",
      })
      .pipe(first());
  }

  exportEntityDocumentsBackupZip(entityId: number) {
    return this.http
      .get(`${environment.apiUrl}/entities/${entityId}/backup/export-documents-zip`, {
        withCredentials: true,
        observe: "response",
        responseType: "blob",
      })
      .pipe(first());
  }
}
